<template>
  <div class="cron-config">
    <div class="cron-input-wrapper">
      <label v-if="label">{{ label }}</label>
      <div class="input-with-icon">
        <input v-model="cronExpression" type="text" placeholder="* * * * *" :disabled="disabled" @input="updateCron" @blur="validateCron" />
        <button v-if="!disabled" type="button" class="icon-button" title="Open Cron Builder" @click="showModal = true">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.5 2.5L13.5 4.5L11.5 6.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M2.5 8H13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M4.5 13.5L2.5 11.5L4.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
      <div v-if="cronError" class="error">{{ cronError }}</div>
    </div>

    <p-modal :visible="showModal">
      <template #title>Cron Expression Builder</template>

      <div class="modal-content">
        <div class="cron-builder">
          <div class="cron-field">
            <label>Minute (0-59)</label>
            <input v-model="cronParts.minute" type="text" placeholder="*" @input="buildCron" />
          </div>

          <div class="cron-field">
            <label>Hour (0-23)</label>
            <input v-model="cronParts.hour" type="text" placeholder="*" @input="buildCron" />
          </div>

          <div class="cron-field">
            <label>Day of Month (1-31)</label>
            <input v-model="cronParts.dayOfMonth" type="text" placeholder="*" @input="buildCron" />
          </div>

          <div class="cron-field">
            <label>Month (1-12)</label>
            <input v-model="cronParts.month" type="text" placeholder="*" @input="buildCron" />
          </div>

          <div class="cron-field">
            <label>Day of Week (0-6)</label>
            <input v-model="cronParts.dayOfWeek" type="text" placeholder="*" @input="buildCron" />
          </div>
        </div>

        <div class="cron-preview">
          <label>Current Expression:</label>
          <code>{{ cronExpression }}</code>
        </div>

        <div v-if="cronDescription" class="cron-preview">
          <label>Schedule Preview:</label>
          <p>{{ cronDescription }}</p>
        </div>
      </div>

      <template #footer>
        <p-button type="button" @click="showModal = false">Cancel</p-button>
        <p-button type="button" color="primary" @click="applyAndClose">Apply</p-button>
      </template>
    </p-modal>
  </div>
</template>

<script>
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';

export default {
  components: {
    'p-button': Button,
    'p-modal': Modal
  },
  props: {
    value: {
      type: String,
      default: '* * * * *'
    },
    label: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      cronExpression: this.value || '* * * * *',
      cronParts: {
        minute: '*',
        hour: '*',
        dayOfMonth: '*',
        month: '*',
        dayOfWeek: '*'
      },
      cronError: '',
      showModal: false
    };
  },
  computed: {
    cronDescription() {
      return this.describeCron(this.cronExpression);
    }
  },
  watch: {
    value(newVal) {
      this.cronExpression = newVal || '* * * * *';
      this.parseCron();
    }
  },
  mounted() {
    this.parseCron();
  },
  methods: {
    updateCron() {
      this.$emit('input', this.cronExpression);
      this.$emit('change', this.cronExpression);
      this.parseCron();
    },
    parseCron() {
      const parts = this.cronExpression.trim().split(/\s+/);
      if (parts.length >= 5) {
        this.cronParts.minute = parts[0] || '*';
        this.cronParts.hour = parts[1] || '*';
        this.cronParts.dayOfMonth = parts[2] || '*';
        this.cronParts.month = parts[3] || '*';
        this.cronParts.dayOfWeek = parts[4] || '*';
      }
    },
    buildCron() {
      this.cronExpression = [
        this.cronParts.minute,
        this.cronParts.hour,
        this.cronParts.dayOfMonth,
        this.cronParts.month,
        this.cronParts.dayOfWeek
      ].join(' ');
      this.updateCron();
    },
    validateCron() {
      this.cronError = '';
      const parts = this.cronExpression.trim().split(/\s+/);

      if (parts.length < 5) {
        this.cronError = 'Cron expression must have at least 5 fields';
        return false;
      }

      const validations = [
        { field: 'minute', value: parts[0], min: 0, max: 59 },
        { field: 'hour', value: parts[1], min: 0, max: 23 },
        { field: 'day of month', value: parts[2], min: 1, max: 31 },
        { field: 'month', value: parts[3], min: 1, max: 12 },
        { field: 'day of week', value: parts[4], min: 0, max: 6 }
      ];

      for (const validation of validations) {
        if (!this.validateField(validation.value, validation.min, validation.max)) {
          this.cronError = `Invalid ${validation.field}: ${validation.value}`;
          return false;
        }
      }

      return true;
    },
    validateField(value, min, max) {
      if (value === '*') return true;

      // Handle ranges (e.g., 1-5)
      if (value.includes('-')) {
        const [start, end] = value.split('-').map(Number);
        return !isNaN(start) && !isNaN(end) && start >= min && end <= max && start <= end;
      }

      // Handle lists (e.g., 1,3,5)
      if (value.includes(',')) {
        return value.split(',').every(v => this.validateField(v.trim(), min, max));
      }

      // Handle steps (e.g., */5)
      if (value.includes('/')) {
        const [range, step] = value.split('/');
        return (range === '*' || this.validateField(range, min, max)) && !isNaN(Number(step));
      }

      // Handle single values
      const num = Number(value);
      return !isNaN(num) && num >= min && num <= max;
    },
    describeCron(cron) {
      const parts = cron.trim().split(/\s+/);
      if (parts.length < 5) return '';

      const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
      let description = 'Runs ';

      // Special cases
      if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        return 'Runs every minute';
      }

      if (minute === '0' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        return 'Runs every hour';
      }

      if (minute === '0' && hour === '0' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        return 'Runs daily at midnight';
      }

      // Build description
      if (minute !== '*') {
        description += `at minute ${minute} `;
      }
      if (hour !== '*') {
        description += `of hour ${hour} `;
      }
      if (dayOfMonth !== '*') {
        description += `on day ${dayOfMonth} `;
      }
      if (month !== '*') {
        const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthNum = Number(month);
        description += `in ${monthNames[monthNum] || `month ${month}`} `;
      }
      if (dayOfWeek !== '*') {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const dayNum = Number(dayOfWeek);
        description += `on ${dayNames[dayNum] || `day ${dayOfWeek}`} `;
      }

      return description.trim() || 'Custom schedule';
    },
    applyAndClose() {
      this.validateCron();
      if (!this.cronError) {
        this.showModal = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.cron-config {
  .cron-input-wrapper {
    display: grid;
    grid-template-rows: max-content max-content max-content;
    grid-gap: 5px;

    label {
      font-weight: 500;
      font-size: 0.75rem;
      letter-spacing: 0.025em;
    }

    .input-with-icon {
      display: flex;
      align-items: center;
      position: relative;

      input {
        flex: 1;
        padding: 8px 40px 8px 12px;
        font-size: 0.85rem;
        font-weight: 400;
        font-family: inherit;
        border-radius: 2px;
        border: 1px solid var(--theme-on-background-dark);
        color: var(--theme-on-background);
        background: var(--theme-background);
        outline: none;
        transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

        &:not([disabled]) {
          &:hover {
            border-color: var(--theme-on-background);
            color: var(--theme-on-background);
          }

          &:focus,
          &:active {
            border-color: var(--theme-on-background);
            color: var(--theme-on-background);
            outline: none;
          }
        }

        &[disabled] {
          opacity: 0.5;
        }
      }

      .icon-button {
        position: absolute;
        right: 4px;
        padding: 6px;
        background: transparent;
        border: none;
        cursor: pointer;
        color: var(--theme-on-background-dark);
        transition: color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
        border-radius: 2px;

        &:hover {
          color: var(--theme-on-background);
          background: var(--theme-on-background-dark);
          background: rgba(0, 0, 0, 0.04);
        }

        &:focus {
          outline: none;
          background: rgba(0, 0, 0, 0.08);
        }

        svg {
          display: block;
        }
      }
    }

    .error {
      color: var(--theme-error);
      font-size: 0.75rem;
      margin-top: 0.25rem;
    }
  }
}

.modal-content {
  padding: 1.5rem 2rem;

  .cron-builder {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    grid-gap: 1rem;
    margin-bottom: 1.5rem;

    .cron-field {
      display: grid;
      grid-template-rows: max-content max-content;
      grid-gap: 5px;

      label {
        font-weight: 500;
        font-size: 0.75rem;
        letter-spacing: 0.025em;
      }

      input {
        padding: 8px 12px;
        font-size: 0.85rem;
        font-weight: 400;
        font-family: inherit;
        border-radius: 2px;
        border: 1px solid var(--theme-on-background-dark);
        color: var(--theme-on-background);
        background: var(--theme-background);
        outline: none;
        transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

        &:hover {
          border-color: var(--theme-on-background);
          color: var(--theme-on-background);
        }

        &:focus,
        &:active {
          border-color: var(--theme-on-background);
          color: var(--theme-on-background);
          outline: none;
        }
      }
    }
  }

  .cron-preview {
    padding: 0.75rem;
    border-radius: 4px;
    background: var(--theme-surface);
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }

    label {
      font-weight: 700;
      font-size: 0.8rem;
      color: var(--theme-on-surface-accent);
    }

    code {
      display: block;
      margin-top: 0.25rem;
      font-family: monospace;
      font-size: 0.9rem;
      color: var(--theme-on-surface);
    }

    p {
      margin: 0.25rem 0 0 0;
      font-size: 0.85rem;
      color: var(--theme-on-surface);
    }
  }
}
</style>
